import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { WAREHOUSE_COORDINATES } from "../config/warehouse";
import { PORT_TO_WAREHOUSE_ROUTES } from "../config/portToWarehouseRoutes";
import {
  geocodeAddress,
  type AddressInput,
} from "../services/distanceService";
import { getRoadDistanceKm } from "../services/distanceService";

export type DeliveryDestination =
  | { type: "coordinates"; lat: number; lon: number; formatted?: string }
  | { type: "address"; address: AddressInput };

export interface UsePortToAddressDistanceParams {
  destinationPortId: number | null;
  deliveryType: "delivery" | "storage";
  /** For delivery: coordinates (from autocomplete) or address (to geocode). For storage: ignored. */
  deliveryDestination: DeliveryDestination | null;
}

export interface UsePortToAddressDistanceResult {
  distanceKm: number | null;
  loading: boolean;
  error: string | null;
  unknownKm: boolean;
  routeGeometry: number[][];
  portName: string | null;
  destLabel: string | null;
  portCoords: { lat: number; lon: number } | null;
  destCoords: { lat: number; lon: number } | null;
}

/**
 * Calculates road distance (km) from destination port to delivery address or warehouse.
 * For Seafreight FCL only.
 */
export function usePortToAddressDistance({
  destinationPortId,
  deliveryType,
  deliveryDestination,
}: UsePortToAddressDistanceParams): UsePortToAddressDistanceResult {
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unknownKm, setUnknownKm] = useState(false);
  const [routeGeometry, setRouteGeometry] = useState<number[][]>([]);
  const [portName, setPortName] = useState<string | null>(null);
  const [destLabel, setDestLabel] = useState<string | null>(null);
  const [portCoords, setPortCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [destCoords, setDestCoords] = useState<{ lat: number; lon: number } | null>(null);

  const compute = useCallback(async () => {
    if (!destinationPortId) {
      setDistanceKm(null);
      setUnknownKm(false);
      setError(null);
      setRouteGeometry([]);
      setPortName(null);
      setDestLabel(null);
      setPortCoords(null);
      setDestCoords(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUnknownKm(false);
    // Keep portCoords, destCoords, portName, destLabel, routeGeometry until new data arrives
    // to prevent map from flashing when recomputing

    try {
      const { data: port, error: portError } = await supabase
        .from("booking_destination_port")
        .select("id, name, latitude, longitude")
        .eq("id", destinationPortId)
        .single();

      if (portError || !port) {
        setDistanceKm(null);
        setError(portError?.message ?? "Port not found");
        setLoading(false);
        return;
      }

      const lat = port?.latitude != null ? Number(port.latitude) : null;
      const lon = port?.longitude != null ? Number(port.longitude) : null;

      if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) {
        setDistanceKm(null);
        setUnknownKm(true);
        setPortName(port?.name ?? null);
        setLoading(false);
        return;
      }

      const coords = { lat, lon };
      setPortCoords(coords);
      setPortName(port?.name ?? null);

      let dest: { lat: number; lon: number; label: string };

      if (deliveryType === "storage") {
        dest = {
          ...WAREHOUSE_COORDINATES,
          label: "E-freight warehouse (Amersfoort)",
        };
        // Use hardcoded route if available - no API call
        const hardcoded = PORT_TO_WAREHOUSE_ROUTES[destinationPortId];
        if (hardcoded && hardcoded.routeGeometry.length >= 2 && hardcoded.distanceKm > 0) {
          setDestCoords({ lat: dest.lat, lon: dest.lon });
          setDestLabel(dest.label);
          setDistanceKm(hardcoded.distanceKm);
          setRouteGeometry(hardcoded.routeGeometry);
          setLoading(false);
          return;
        }
      } else if (deliveryDestination) {
        if (deliveryDestination.type === "coordinates") {
          dest = {
            lat: deliveryDestination.lat,
            lon: deliveryDestination.lon,
            label: deliveryDestination.formatted ?? "Delivery address",
          };
        } else {
          const geocoded = await geocodeAddress(deliveryDestination.address);
          if (!geocoded) {
            setDistanceKm(null);
            setError("Could not geocode address");
            setLoading(false);
            return;
          }
          dest = { ...geocoded, label: "Delivery address" };
        }
      } else {
        // deliveryType is "delivery" but no address yet - don't clear existing state
        // to prevent map from disappearing during re-renders
        setLoading(false);
        return;
      }

      setDestCoords({ lat: dest.lat, lon: dest.lon });
      setDestLabel(dest.label);

      const result = await getRoadDistanceKm(coords, dest);
      if (result == null) {
        setDistanceKm(null);
        setError("Could not calculate distance");
      } else {
        setDistanceKm(result.distanceKm);
        setRouteGeometry(result.routeGeometry ?? []);
      }
    } catch (err) {
      console.error("usePortToAddressDistance error:", err);
      setDistanceKm(null);
      setError(err instanceof Error ? err.message : "Distance calculation failed");
    } finally {
      setLoading(false);
    }
  }, [destinationPortId, deliveryType, deliveryDestination]);

  useEffect(() => {
    compute();
  }, [compute]);

  return {
    distanceKm,
    loading,
    error,
    unknownKm,
    routeGeometry,
    portName,
    destLabel,
    portCoords,
    destCoords,
  };
}
